import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';

const REMS_HTTPS_SETTINGS_API = `${config.backendUrl}/api/rems-https-settings`;

const defaultForm = {
  enabled: false,
  cid: '',
  authorization: '',
  multi: 0,
};

export default function RemsHttpsSetting() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const applyResponseToForm = useCallback((data) => {
    if (!data || typeof data !== 'object') return;

    setForm({
      enabled: Boolean(data.enabled ?? data.rems_https_enabled),
      cid: (data.cid ?? '').toString(),
      authorization: (data.authorization ?? '').toString(),
      multi:
        data.multi !== undefined && data.multi !== null && data.multi !== ''
          ? Number(data.multi)
          : 0,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const res = await axios.get(REMS_HTTPS_SETTINGS_API);

        if (!cancelled && res.data) {
          applyResponseToForm(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('REMS HTTPS 설정 조회 실패:', e);

          if (e.response?.status === 404) {
            alert('REMS HTTPS 설정 API가 아직 구현되지 않았습니다.');
          } else {
            alert('설정을 불러오지 못했습니다.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [applyResponseToForm]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.enabled) return true;

    if (!form.cid.trim()) {
      alert('전송을 사용할 때는 CID를 입력하세요.');
      return false;
    }

    if (!form.authorization.trim()) {
      alert('전송을 사용할 때는 Authorization(API 인증 키)를 입력하세요.');
      return false;
    }

    const m = Number(form.multi);

    if (!Number.isFinite(m) || m < 0 || !Number.isInteger(m)) {
      alert('확장 수(multi)는 0 이상의 정수여야 합니다.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);

    const payload = {
      enabled: Boolean(form.enabled),
      cid: form.cid.trim(),
      authorization: form.authorization.trim(),
      multi: Number(form.multi),
    };

    try {
      await axios.post(REMS_HTTPS_SETTINGS_API, payload);

      alert('REMS HTTPS 설정이 저장되었습니다.');
    } catch (err) {
      console.error('REMS HTTPS 설정 저장 실패:', err);

      const status = err.response?.status;
      const detail =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;

      alert(
        status === 404
          ? '저장 API가 아직 없습니다. 백엔드에 POST /api/rems-https-settings 를 구현해 주세요.'
          : `저장에 실패했습니다. ${detail || ''}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMultiChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      handleChange('multi', 0);
      return;
    }

    const numberValue = parseInt(value, 10);
    handleChange('multi', Number.isFinite(numberValue) ? numberValue : 0);
  };

  if (loading) {
    return (
      <div className="rems-setting-loading">
        <Spinner animation="border" size="sm" />
        <span>설정 불러오는 중…</span>
      </div>
    );
  }

  return (
    <div className="rems-setting-wrap">
      <style>
        {`
          .rems-setting-wrap {
            width: 100%;
            max-width: 860px;
            padding: 6px 0 4px;
          }

          .rems-setting-loading {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 0;
            color: #6c757d;
            font-size: 14px;
          }

          .rems-setting-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 18px;
            padding: 14px 16px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #f8fafc;
          }

          .rems-setting-title-box {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .rems-setting-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }

          .rems-setting-desc {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.4;
          }

          .rems-setting-switch {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }

          .rems-setting-switch .form-check {
            margin: 0;
            min-height: 0;
            padding-left: 0;
          }

          .rems-setting-switch .form-check-input {
            width: 56px;
            height: 28px;
            margin: 0;
            cursor: pointer;
          }

          .rems-setting-switch .form-check-label {
            display: none;
          }

          .rems-switch-text {
            font-size: 14px;
            font-weight: 700;
            color: #2563eb;
            min-width: 48px;
          }

          .rems-switch-text.off {
            color: #6b7280;
          }

          .rems-setting-card {
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 18px;
            background: #ffffff;
          }

          .rems-form-label {
            font-size: 14px;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 7px;
          }

          .rems-form-control {
            max-width: 360px;
            height: 38px;
            font-size: 14px;
          }

          .rems-auth-control {
            max-width: 480px;
          }

          .rems-form-help {
            display: block;
            margin-top: 5px;
            font-size: 12px;
            color: #6b7280;
          }

          .rems-save-area {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 16px;
          }

          .rems-save-button {
            min-width: 82px;
            height: 38px;
            font-weight: 700;
          }

          @media (max-width: 768px) {
            .rems-setting-wrap {
              max-width: 100%;
            }

            .rems-setting-header {
              align-items: flex-start;
              flex-direction: column;
            }

            .rems-setting-switch {
              width: 100%;
              justify-content: flex-start;
            }

            .rems-form-control,
            .rems-auth-control {
              max-width: 100%;
            }
          }
        `}
      </style>

      <Form onSubmit={handleSubmit}>
        <div className="rems-setting-header">
          <div className="rems-setting-title-box">
            <div className="rems-setting-title">REMS HTTPS 전송 설정</div>
            <div className="rems-setting-desc">
              REMS 서버로 태양광 데이터를 주기적으로 전송하기 위한 인증 정보를 설정합니다.
            </div>
          </div>

          <div className="rems-setting-switch">
            <Form.Check
              type="switch"
              id="rems-https-enabled"
              checked={form.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
            />
            <span className={`rems-switch-text ${form.enabled ? '' : 'off'}`}>
              {form.enabled ? '사용' : '미사용'}
            </span>
          </div>
        </div>

        <div className="rems-setting-card">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="remsHttpsCid">
                <Form.Label className="rems-form-label">CID</Form.Label>
                <Form.Control
                  className="rems-form-control"
                  type="text"
                  placeholder="예: 10205000002"
                  value={form.cid}
                  onChange={(e) => handleChange('cid', e.target.value)}
                  disabled={!form.enabled}
                  autoComplete="off"
                />
                <Form.Text className="rems-form-help">
                  REMS 설비 등록 시 생성되는 CID입니다.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="remsHttpsMulti">
                <Form.Label className="rems-form-label">확장 수 / multi</Form.Label>
                <Form.Control
                  className="rems-form-control"
                  type="number"
                  min={0}
                  step={1}
                  value={form.multi}
                  onChange={handleMultiChange}
                  disabled={!form.enabled}
                />
                <Form.Text className="rems-form-help">
                  설비 1은 0, 설비 2는 1로 입력합니다.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="remsHttpsAuth">
                <Form.Label className="rems-form-label">
                  Authorization / API 인증 키
                </Form.Label>

                <InputGroup className="rems-auth-control">
                  <Form.Control
                    type={showAuth ? 'text' : 'password'}
                    placeholder="REMS에서 발급받은 Authorization 값"
                    value={form.authorization}
                    onChange={(e) => handleChange('authorization', e.target.value)}
                    disabled={!form.enabled}
                    autoComplete="new-password"
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    disabled={!form.enabled}
                    onClick={() => setShowAuth((prev) => !prev)}
                  >
                    {showAuth ? '숨김' : '보기'}
                  </Button>
                </InputGroup>

                <Form.Text className="rems-form-help">
                  인증 API 요청 시 Header의 Authorization 값으로 사용됩니다.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="rems-save-area">
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              className="rems-save-button"
            >
              {saving ? '저장 중…' : '저장'}
            </Button>

            {!form.enabled && (
              <span className="rems-form-help">
                전송 사용을 켜야 입력값을 수정할 수 있습니다.
              </span>
            )}
          </div>
        </div>
      </Form>
    </div>
  );
}